import { ArrowRight, Sparkles, ExternalLink } from 'lucide-react';
import SuiteIcon from '../SuiteIcon';

import { useLanguage } from '../../context/LanguageContext';
/**
 * ProfessionalPitch
 * Premium "Trust Creator Suite" pitch block — used on the About page and
 * anywhere we want to showcase the LWT/TD/TVD ecosystem story.
 */
const ProfessionalPitch = ({ language = 'ar' }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });

  const pillars = [
    {
      app: 'lwt',
      name: 'Learn With Trust',
      short: 'LWT',
      tag: t('التعلّم', 'Learn'),
      desc: t('احجز معلّمك الموثوق وابدأ رحلتك التعليمية.', 'Book your verified tutor and start your learning journey.'),
      color: 'indigo',
      url: null, // current site
    },
    {
      app: 'td',
      name: 'Trust Design',
      short: 'TD',
      tag: t('النشر', 'Publish'),
      desc: t('حوّل معرفتك إلى كتاب احترافي على Amazon.', 'Transform your knowledge into a professional Amazon book.'),
      color: 'emerald',
      url: 'https://td-trustdesign.com',
    },
    {
      app: 'tvd',
      name: 'Trust Visual Director',
      short: 'TVD',
      tag: t('الإنتاج', 'Produce'),
      desc: t('حوّل قصتك إلى فيلم سينمائي عالي الجودة.', 'Turn your story into a high‑fidelity cinematic film.'),
      color: 'amber',
      url: 'https://trusttvd.com',
    },
  ];

  const palette = {
    indigo:  { ring: 'ring-indigo-500/30',  bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-300',  badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
    emerald: { ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-300', badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    amber:   { ring: 'ring-amber-500/30',   bg: 'bg-amber-500/10',   text: 'text-amber-600 dark:text-amber-300',     badge: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white py-12 sm:py-14"
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="professional-pitch"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            'radial-gradient(700px 400px at 20% 30%, rgba(99,102,241,0.18), transparent 60%),' +
            'radial-gradient(600px 350px at 80% 70%, rgba(245,158,11,0.10), transparent 60%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        {/* Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 backdrop-blur-md text-white/80 text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3 h-3" />
            {t('عرضنا الاحترافي', 'The Professional Pitch')}
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight mb-3 leading-tight">
            {t('منظومة المبدعين المتكاملة', 'The Complete Creator Suite')}
          </h2>

          {/* Pitch paragraph — concise */}
          <p className="text-sm sm:text-base text-white/75 leading-relaxed max-w-2xl mx-auto">
            {t(
              'منظومة Trust Creator Suite تربط Learn With Trust بـ Trust Design (TD) و TVD — من أول حصة إلى النشر العالمي والإنتاج السينمائي.',
              'Trust Creator Suite links Learn With Trust with Trust Design (TD) and TVD — from the first lesson to global publishing and cinematic production.'
            )}
          </p>

          <a
            href="https://www.trustedtechno.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 text-xs sm:text-sm font-bold hover:-translate-y-0.5 transition-all shadow-[0_8px_30px_rgba(255,255,255,0.15)]"
            data-testid="pitch-trustedtechno-cta"
          >
            www.TrustEdTechno.com
            <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
          </a>
        </div>

        {/* 3 pillars — compact */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-8">
          {pillars.map((p) => {
            const c = palette[p.color];
            const Wrapper = p.url ? 'a' : 'div';
            const wrapperProps = p.url
              ? {
                  href: p.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(p.url, '_blank', 'noopener,noreferrer');
                  },
                }
              : {};
            return (
              <Wrapper
                key={p.short}
                {...wrapperProps}
                className={`group relative p-6 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md hover:bg-white/[0.07] transition-all hover:-translate-y-0.5 ring-1 ${c.ring} ${p.url ? 'cursor-pointer block' : ''}`}
                data-testid={`pitch-pillar-${p.short.toLowerCase()}`}
              >
                {/* External-link hint on hover */}
                {p.url && (
                  <ExternalLink className="absolute top-3 end-3 w-3.5 h-3.5 text-white/30 group-hover:text-white/70 transition-colors" />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center p-1.5`}>
                    <SuiteIcon app={p.app} size={36} />
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.badge}`}>
                    {p.tag}
                  </span>
                </div>
                <div className="text-lg font-black text-white">
                  {p.name}
                  <span className="ms-2 text-[11px] font-mono text-white/40">{p.short}</span>
                </div>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{p.desc}</p>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProfessionalPitch;
